// grading/grade.ts - the grading orchestrator (ENGINE_DESIGN.md §7). Ties the EV estimate, grade
// bands + modifiers, bet-math verdict, sizing fit, error-code → dimension attribution, and the fading
// scaffold signal into ONE graded UserAction per decision. Pure function of its inputs.
//
// What gets graded (§7.1): (1) action choice vs the EV-ranked list, (2) sizing-intent fit, (3) bet-math
// arithmetic, (4) skill dimensions from error codes. arithmeticVerdict and sizingFit are stored
// SEPARATELY (not just folded into the grade) so the Progress engine can split bad poker from bad math.
import { SizingIntentSchema, } from '@postflop/schema';
import { ruleMatches } from '../strategy/match';
import { estimateEv } from './ev';
import { gradeAbstention, gradeFromEvLoss } from './bands';
import { gradeBetMath } from './bet-math';
import { attributeDimensions, deriveErrorCodes } from './dimensions';
import { scaffoldLevel } from './scaffold';
/** Full sizing-intent ladder (mirrors SizingIntentSchema order); the ordinal gap drives sizing fit +
 *  the sizing modifier. Kept in sync with the resolver's ladder by construction from the schema enum. */
const SIZING_LADDER = SizingIntentSchema.options;
const sizingOrd = (s) => SIZING_LADDER.indexOf(s);
const isBetting = (a) => a === 'bet' || a === 'raise' || a === 'jam';
/** Sizing fit + step gap between the user's intent and the recommendation's (§7.1: fits within ±1 step).
 *  Only meaningful when BOTH the user's action and the recommended action are sized bets of the same
 *  kind; otherwise there is no sizing to compare (steps 0, fit 'fits'). */
const sizingComparison = (user, recommendation) => {
    const bothBetting = isBetting(user.action) &&
        user.action === recommendation.finalAction &&
        user.sizingIntent !== undefined &&
        recommendation.sizingIntent !== undefined;
    if (!bothBetting)
        return { steps: 0, fit: 'fits' };
    const uo = sizingOrd(user.sizingIntent);
    const ro = sizingOrd(recommendation.sizingIntent);
    const steps = Math.abs(uo - ro);
    const fit = steps <= 1 ? 'fits' : uo < ro ? 'too_small' : 'too_big';
    return { steps, fit };
};
/** Did the user's action get forbidden by a HIGH-confidence rule that matched this spot? Drives the
 *  §7.3 punt override. Recomputed from ctx.rules via ruleMatches (deterministic, same as the resolver).
 *  Only counts when the user actually chose the forbidden action AND deviated from the recommendation. */
const forbiddenByHighConfidence = (ctx, recommendation, user) => {
    if (user.action === recommendation.finalAction)
        return false;
    for (const rule of ctx.rules) {
        if (rule.confidence !== 'high')
            continue;
        if (!ruleMatches(rule, ctx))
            continue;
        if (rule.effect.action?.forbid?.includes(user.action))
            return true;
    }
    return false;
};
/**
 * Grade one decision. Runs (or accepts) the EV estimate, maps it to a band, applies the sizing +
 * arithmetic modifiers and the forbidden-vs-high-confidence punt, attributes error codes → dimensions,
 * and derives the fading-scaffold level. Returns everything the persistence + coach layers need.
 */
export const gradeDecision = (input) => {
    const { ctx, recommendation, user } = input;
    const ev = input.ev ?? estimateEv(ctx, user, input.evInputs);
    // ── bet-math arithmetic (stored separately, §7.3) ──────────────────────────────
    let arithmetic;
    let arithmeticVerdict;
    if (isBetting(user.action) && user.sizingIntent !== undefined && user.typedAmount !== undefined) {
        const g = gradeBetMath(user.sizingIntent, user.typedAmount, ctx.price.pot, ctx.price.toCall, input.betMathExpectedOverride);
        if (g) {
            arithmetic = g;
            arithmeticVerdict = g.verdict;
        }
    }
    // ── sizing fit + step gap ──────────────────────────────────────────────────────
    const { steps: sizingSteps, fit: sizingFit } = sizingComparison(user, recommendation);
    // ── the composite grade ────────────────────────────────────────────────────────
    const forbiddenHighConfidence = forbiddenByHighConfidence(ctx, recommendation, user);
    const mods = { sizingSteps, arithmeticVerdict, forbiddenHighConfidence };
    let graded;
    if (ev.abstained) {
        const matchedFinal = user.action === recommendation.finalAction;
        const plausible = matchedFinal || recommendation.alternatives.some((a) => a.action === user.action);
        graded = gradeAbstention(matchedFinal, plausible, mods);
    }
    else {
        graded = gradeFromEvLoss(ev.evLossBB ?? 0, mods);
    }
    // ── error codes → dimensions ───────────────────────────────────────────────────
    const errorCodes = deriveErrorCodes(ctx, recommendation, user);
    if (arithmeticVerdict === 'off' && !errorCodes.includes('ARITH_OFF'))
        errorCodes.push('ARITH_OFF');
    const skillScores = attributeDimensions(errorCodes, ev.evLossBB, graded.score);
    // ── fading-scaffold signal ──────────────────────────────────────────────────────
    const trailing = [...(input.trailingArithmetic ?? [])];
    if (arithmeticVerdict)
        trailing.push(arithmeticVerdict);
    const scaffold = scaffoldLevel(trailing);
    const firedRuleIds = recommendation.firedRules.map((f) => f.id);
    const userAction = {
        action: user.action,
        sizingIntent: user.sizingIntent,
        typedAmount: user.typedAmount,
        arithmeticVerdict,
        sizingFit,
        grade: graded.score,
        gradeBand: graded.band,
        evLossBB: ev.evLossBB,
        skillScores,
        errorCodes,
        firedRuleIds,
    };
    return {
        grade: graded.score,
        gradeBand: graded.band,
        evLossBB: ev.evLossBB,
        arithmeticVerdict,
        arithmetic,
        sizingFit,
        sizingSteps,
        skillScores,
        errorCodes,
        firedRuleIds,
        scaffoldLevel: scaffold,
        abstained: ev.abstained,
        userAction,
    };
};
//# sourceMappingURL=grade.js.map